import styled from 'styled-components';
import { Box, Heading, Paragraph, Stack } from '@99designs/emu';
import { colors, spaces } from '@99designs/emu/settings';
import { GridLayout, Scroll } from '@99designs/marketing-ui';
import { CollaborationMethod } from '../../types';
import { FeatureItem } from './FeatureItem';
import { ProductCard } from './ProductCard';
import { mergeArraysByIndex } from './mergeArraysByIndex';
import type { ComparativeProduct, ProductFeature } from '../../types';

const Grid = styled(Box)`
    grid-template-columns: repeat(4, 1fr);
    grid-column-gap: ${spaces['1bu']};
    grid-row-gap: ${spaces['0.75bu']};

    > * {
        width: 300px;
    }
`;

const FeatureItemWrapper = styled(Box)`
    border-bottom: 1px solid ${colors.grey300};
`;

export interface ProductComparisonProps {
    products: ComparativeProduct[];
}

export function ProductComparison({ products }: ProductComparisonProps) {
    const featureLists = products.map((product) => product.features);

    // This assumes that every product has the same amount of features
    const allProductFeatures = mergeArraysByIndex(featureLists) as ProductFeature[];

    const isContestProduct = products
        .map((product) => {
            return product.collaborationMethod === CollaborationMethod.Contest;
        })
        .every((value, _, products) => value === products[0]);

    return (
        <Stack space="1bu">
            <GridLayout>
                <Stack space="0.5bu">
                    <Heading appearanceLevel={4} accessibilityLevel={2}>
                        {__('Simple pricing for every budget')}
                    </Heading>
                    <Paragraph>{__('Choose a package that suits your needs')}</Paragraph>
                </Stack>
            </GridLayout>
            <Scroll controlsEnabled gridWidth={1300}>
                <Grid
                    display="grid"
                    paddingLeft={['none', '0.75bu', '1bu', '1bu']}
                    paddingRight={['none', '0.75bu', '1bu', '1bu']}
                >
                    {products.map((product) => {
                        return (
                            <ProductCard
                                key={product.key}
                                product={product}
                                isContestProduct={isContestProduct}
                            />
                        );
                    })}
                </Grid>
                <Grid
                    display="grid"
                    paddingLeft={['none', '0.75bu', '1bu', '1bu']}
                    paddingRight={['none', '0.75bu', '1bu', '1bu']}
                    paddingTop="4bu"
                >
                    {allProductFeatures.map((feature, i) => {
                        return (
                            <FeatureItemWrapper key={i} paddingBottom="0.75bu">
                                <FeatureItem feature={feature} />
                            </FeatureItemWrapper>
                        );
                    })}
                </Grid>
            </Scroll>
        </Stack>
    );
}
